import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import { esignatureService } from "../../services/esignature";
import {
  Card,
  Text,
  Group,
  Stack,
  Loader,
  Alert,
  Title,
  Tabs,
  Button,
  Badge,
  Modal,
  TextInput,
  Switch,
  Checkbox,
  MultiSelect,
  NumberInput,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertCircle,
  IconRefresh,
  IconPlus,
  IconEdit,
  IconTrash,
  IconLock,
  IconFileText,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PageContent from "../shared/PageContent/PageContent";
import CollapsibleCard from "./CollapsibleCard";

const SortableTab = ({ year, isActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: year.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    display: "inline-flex",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Tabs.Tab value={year.name} style={{ fontWeight: isActive ? 700 : 400 }}>
        {year.name}
        {year.is_active && (
          <span style={{ color: "orange", marginLeft: 4 }}>*</span>
        )}
        {year.is_locked && (
          <IconLock
            size={14}
            color="gray"
            style={{ marginLeft: 4, verticalAlign: "middle" }}
          />
        )}
      </Tabs.Tab>
    </div>
  );
};

// Default scales are grouped into these sections (in this order) on the copy
// step.
const SCALE_TYPE_SECTIONS = [
  { type: "COMMISSION", label: "Commission" },
  { type: "OVERRIDE", label: "Override" },
  { type: "MD", label: "MD" },
];

const emptyMdScaleLine = () => ({ revenue_amount: "", percentage: "" });
const emptyMdScale = () => ({
  id: null,
  scale_lines: [emptyMdScaleLine()],
  marketing_partner_ids: [],
});

const formatScaleCurrency = value =>
  value === "" || value == null
    ? "$0.00"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatScalePercent = value =>
  value === "" || value == null ? "0%" : `${value}%`;

const formatDisplayDate = dateString => {
  if (!dateString) return "N/A";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  const d = match
    ? new Date(
        parseInt(match[1], 10),
        parseInt(match[2], 10) - 1,
        parseInt(match[3], 10)
      )
    : new Date(dateString);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Label maps mirror the dedicated upfront/expense/bonus sections so the copy
// tiles read the same.
const PAY_TYPE_LABELS = {
  FIXED_AMOUNT_PER_ACCOUNT: "Fixed Amount Per Account",
  SCALE_AMOUNT_PER_ACCOUNT: "Scaled Amount Per Account",
  SCALE_AMOUNT_TOTAL: "Scaled Amount Total",
  FIXED_PERCENT_ACV: "Fixed Percentage of ACV",
  SCALE_PERCENT_ACV: "Scaled Percentage of ACV",
  INITIAL_PRICE: "Initial Price",
};

const EXPENSE_TYPE_LABELS = {
  BACKEND_PERCENTAGE: "Backend Percentage",
  BACKEND_FIXED_AMOUNT: "Backend Fixed Amount",
  FRONTEND_RECURRING: "Frontend Recurring",
};

const RECURRING_SCHEDULE_LABELS = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Biweekly",
  MONTHLY: "Monthly",
};

const EXPENSE_DAY_OF_WEEK_LABELS = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

const BONUS_CATEGORY_LABELS = {
  ITEM: "Item",
  MONEY: "Money",
  RECURRING: "Recurring",
};

const BONUS_EARNED_BY_ITEM_LABELS = {
  REVENUE: "Revenue",
  ACCOUNTS: "Accounts",
  RECRUITS: "Recruits",
};

const BONUS_EARNED_BY_TYPE_LABELS = {
  SERVICED: "Serviced",
  QUALIFIED: "Qualified",
  GET_TO_MARKET: "Get to Market",
  FINISH_SUMMER: "Finish Summer",
};

const BONUS_DAY_OF_WEEK_LABELS = {
  1: "Sunday",
  2: "Monday",
  3: "Tuesday",
  4: "Wednesday",
  5: "Thursday",
  6: "Friday",
  7: "Saturday",
};

const DetailRow = ({ label, value }) => (
  <Group gap="xs">
    <Text size="sm" c="dimmed">
      {label}:
    </Text>
    <Text size="sm">{value}</Text>
  </Group>
);

const renderUpfrontDetails = item => {
  const type = item.upfront_pay_type;
  const isPercentScale = type === "SCALE_PERCENT_ACV";
  const lines = Array.isArray(item.scale_lines) ? item.scale_lines : [];
  return (
    <Stack gap={4}>
      <Text size="sm" c="dimmed">
        {PAY_TYPE_LABELS[type] ?? type}
      </Text>
      {type === "FIXED_AMOUNT_PER_ACCOUNT" && item.fixed_amount != null && (
        <DetailRow
          label="Fixed Amount"
          value={formatScaleCurrency(item.fixed_amount)}
        />
      )}
      {type === "FIXED_PERCENT_ACV" && item.fixed_percentage != null && (
        <DetailRow
          label="Fixed Percentage"
          value={formatScalePercent(item.fixed_percentage)}
        />
      )}
      {lines.length > 0 && (
        <Stack gap={2}>
          {lines.map((line, i) => (
            <Group key={line.id ?? i} gap="xs">
              <Text size="sm" c="dimmed">
                {line.accounts_amount}
              </Text>
              <Text size="sm" c="dimmed">
                →
              </Text>
              <Text size="sm">
                {isPercentScale
                  ? formatScalePercent(line.percentage)
                  : formatScaleCurrency(line.amount)}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const renderExpenseDetails = item => (
  <Group gap="md">
    {item.expense_type_category && (
      <Badge variant="light">
        {EXPENSE_TYPE_LABELS[item.expense_type_category] ??
          item.expense_type_category}
      </Badge>
    )}
    {item.backend_percentage != null && (
      <DetailRow
        label="Backend Percentage"
        value={formatScalePercent(item.backend_percentage)}
      />
    )}
    {item.backend_fixed_amount != null && (
      <DetailRow
        label="Backend Fixed Amount"
        value={formatScaleCurrency(item.backend_fixed_amount)}
      />
    )}
    {item.recurring_frontend_amount != null && (
      <DetailRow
        label="Recurring Amount"
        value={formatScaleCurrency(item.recurring_frontend_amount)}
      />
    )}
    {item.recurring_schedule_type != null && (
      <DetailRow
        label="Schedule"
        value={
          RECURRING_SCHEDULE_LABELS[item.recurring_schedule_type] ??
          item.recurring_schedule_type
        }
      />
    )}
    {item.recurring_day_of_week != null && (
      <DetailRow
        label="Day of Week"
        value={
          EXPENSE_DAY_OF_WEEK_LABELS[item.recurring_day_of_week] ??
          item.recurring_day_of_week
        }
      />
    )}
    {item.recurring_day_of_month != null && (
      <DetailRow label="Day of Month" value={item.recurring_day_of_month} />
    )}
  </Group>
);

const renderBonusDetails = item => {
  const category = item.bonus_type_category;
  return (
    <Group gap="md">
      {category && (
        <Badge variant="light">
          {BONUS_CATEGORY_LABELS[category] ?? category}
        </Badge>
      )}
      {item.bonus_type_earned_by_item != null && (
        <DetailRow
          label="Earned By"
          value={
            BONUS_EARNED_BY_ITEM_LABELS[item.bonus_type_earned_by_item] ??
            item.bonus_type_earned_by_item
          }
        />
      )}
      {item.bonus_type_earned_by_type != null && (
        <DetailRow
          label="Earned By Type"
          value={
            BONUS_EARNED_BY_TYPE_LABELS[item.bonus_type_earned_by_type] ??
            item.bonus_type_earned_by_type
          }
        />
      )}
      {item.bonus_amount != null && (
        <DetailRow
          label="Bonus Amount"
          value={formatScaleCurrency(item.bonus_amount)}
        />
      )}
      {category === "ITEM" && item.item_name != null && (
        <DetailRow label="Item" value={item.item_name} />
      )}
      {category === "ITEM" && item.item_monetary_value != null && (
        <DetailRow
          label="Item Value"
          value={formatScaleCurrency(item.item_monetary_value)}
        />
      )}
      {category === "RECURRING" && item.recurring_initial_amount != null && (
        <DetailRow
          label="Initial Amount"
          value={formatScaleCurrency(item.recurring_initial_amount)}
        />
      )}
      {category === "RECURRING" && item.recurring_repeated_amount != null && (
        <DetailRow
          label="Repeated Amount"
          value={formatScaleCurrency(item.recurring_repeated_amount)}
        />
      )}
      {category === "RECURRING" && item.recurring_schedule_type != null && (
        <DetailRow
          label="Schedule"
          value={
            RECURRING_SCHEDULE_LABELS[item.recurring_schedule_type] ??
            item.recurring_schedule_type
          }
        />
      )}
      {category === "RECURRING" && item.recurring_start_date != null && (
        <DetailRow
          label="Start"
          value={formatDisplayDate(item.recurring_start_date)}
        />
      )}
      {category === "RECURRING" && item.recurring_end_date != null && (
        <DetailRow
          label="End"
          value={formatDisplayDate(item.recurring_end_date)}
        />
      )}
      {category === "RECURRING" && item.recurring_day_of_week != null && (
        <DetailRow
          label="Day of Week"
          value={
            BONUS_DAY_OF_WEEK_LABELS[String(item.recurring_day_of_week)] ??
            item.recurring_day_of_week
          }
        />
      )}
      {category === "RECURRING" && item.recurring_day_of_month != null && (
        <DetailRow label="Day of Month" value={item.recurring_day_of_month} />
      )}
    </Group>
  );
};

// The company deal is the entry flagged is_company_deal. Its MD-type deal_scales
// each carry their own scale_lines plus the marketing partners they apply to.
const buildMdScaleGroups = year => {
  const companyDeal = (year?.company_deals || []).find(d => d.is_company_deal);
  const dealScales = companyDeal?.deal_scales || [];
  return dealScales
    .filter(ds => ds.scale_type === "MD")
    .map(ds => ({
      id: ds.id,
      scale_lines:
        Array.isArray(ds.scale_lines) && ds.scale_lines.length > 0
          ? ds.scale_lines.map(line => ({
              revenue_amount: line.revenue_amount ?? "",
              percentage: line.percentage ?? "",
            }))
          : [emptyMdScaleLine()],
      marketing_partner_ids: Array.isArray(ds.marketing_partners)
        ? ds.marketing_partners.map(mp => String(mp.marketing_partner_id))
        : [],
    }));
};

// Editable list of MD scales. `partnerOptions` is the set of marketing partners
// ({ value, label }) that can be assigned; each partner can belong to only one
// scale, so options already taken by another scale are hidden.
function MdScalesEditor({ scales, onChange, partnerOptions }) {
  const updateMdScale = (scaleIndex, producer) =>
    onChange(
      scales.map((s, i) => (i === scaleIndex ? { ...s, ...producer(s) } : s))
    );

  return (
    <>
      {scales.map((scale, scaleIndex) => (
        <Card key={scaleIndex} padding="md" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600}>
                Scale {scaleIndex + 1}
              </Text>
              {scales.length > 1 && (
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() =>
                    onChange(scales.filter((_, i) => i !== scaleIndex))
                  }
                  aria-label="Remove scale"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>

            <Divider label="Scale Lines" labelPosition="left" />

            <Stack gap="xs">
              {scale.scale_lines.map((line, lineIndex) => (
                <Group key={lineIndex} align="flex-end" gap="xs" wrap="nowrap">
                  <NumberInput
                    label={lineIndex === 0 ? "Revenue Amount" : undefined}
                    placeholder="Revenue amount"
                    value={line.revenue_amount}
                    onChange={value =>
                      updateMdScale(scaleIndex, s => ({
                        scale_lines: s.scale_lines.map((l, li) =>
                          li === lineIndex ? { ...l, revenue_amount: value } : l
                        ),
                      }))
                    }
                    min={0}
                    prefix="$"
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    style={{ flex: 1 }}
                  />
                  <NumberInput
                    label={lineIndex === 0 ? "Percentage" : undefined}
                    placeholder="Percentage"
                    value={line.percentage}
                    onChange={value =>
                      updateMdScale(scaleIndex, s => ({
                        scale_lines: s.scale_lines.map((l, li) =>
                          li === lineIndex ? { ...l, percentage: value } : l
                        ),
                      }))
                    }
                    min={0}
                    suffix="%"
                    style={{ flex: 1 }}
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() =>
                      updateMdScale(scaleIndex, s => ({
                        scale_lines:
                          s.scale_lines.length > 1
                            ? s.scale_lines.filter((_, li) => li !== lineIndex)
                            : s.scale_lines,
                      }))
                    }
                    disabled={scale.scale_lines.length === 1}
                    aria-label="Remove scale line"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Group justify="flex-start">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() =>
                    updateMdScale(scaleIndex, s => ({
                      scale_lines: [...s.scale_lines, emptyMdScaleLine()],
                    }))
                  }
                >
                  Add Scale Line
                </Button>
              </Group>
            </Stack>

            <MultiSelect
              label="Marketing Partners"
              placeholder="Select marketing partners this scale applies to"
              data={partnerOptions.filter(opt => {
                // A marketing partner can only belong to one scale, so hide any
                // already assigned to a different scale.
                const assignedElsewhere = scales.some(
                  (other, i) =>
                    i !== scaleIndex &&
                    other.marketing_partner_ids.includes(opt.value)
                );
                return !assignedElsewhere;
              })}
              value={scale.marketing_partner_ids}
              onChange={value =>
                updateMdScale(scaleIndex, () => ({
                  marketing_partner_ids: value,
                }))
              }
              searchable
              clearable
            />
          </Stack>
        </Card>
      ))}

      <Button
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={() => onChange([...scales, emptyMdScale()])}
      >
        Add Scale
      </Button>
    </>
  );
}

function SellingYearsPage() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [sellingYears, setSellingYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [newYearName, setNewYearName] = useState("");
  const [newYearStartDate, setNewYearStartDate] = useState(null);
  const [newYearEndDate, setNewYearEndDate] = useState(null);
  const [addStep, setAddStep] = useState("details");
  const [copyMarketingPartnerIds, setCopyMarketingPartnerIds] = useState([]);
  const [addMdScales, setAddMdScales] = useState([]);
  const [uploadedAgreements, setUploadedAgreements] = useState([]);
  const [copyAgreementIds, setCopyAgreementIds] = useState([]);
  const [defaultScales, setDefaultScales] = useState([]);
  const [copyScaleIds, setCopyScaleIds] = useState([]);
  const [upfrontPay, setUpfrontPay] = useState([]);
  const [copyUpfrontIds, setCopyUpfrontIds] = useState([]);
  const [defaultExpenses, setDefaultExpenses] = useState([]);
  const [copyExpenseIds, setCopyExpenseIds] = useState([]);
  const [defaultBonuses, setDefaultBonuses] = useState([]);
  const [copyBonusIds, setCopyBonusIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [editYearName, setEditYearName] = useState("");
  const [editYearStartDate, setEditYearStartDate] = useState(null);
  const [editYearEndDate, setEditYearEndDate] = useState(null);
  const [editYearLocked, setEditYearLocked] = useState(false);
  const [editYearActive, setEditYearActive] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [deletingYear, setDeletingYear] = useState(null);
  const [blockEditModalOpened, setBlockEditModalOpened] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editingBlockYearId, setEditingBlockYearId] = useState(null);
  const [blockEditName, setBlockEditName] = useState("");
  const [blockEditStartDate, setBlockEditStartDate] = useState(null);
  const [blockEditEndDate, setBlockEditEndDate] = useState(null);
  const [blockEditLockInDate, setBlockEditLockInDate] = useState(null);
  const [blockEditCompanyIds, setBlockEditCompanyIds] = useState([]);
  const [blockAddModalOpened, setBlockAddModalOpened] = useState(false);
  const [addingBlockYearId, setAddingBlockYearId] = useState(null);
  const [blockAddName, setBlockAddName] = useState("");
  const [blockAddStartDate, setBlockAddStartDate] = useState(null);
  const [blockAddEndDate, setBlockAddEndDate] = useState(null);
  const [blockAddLockInDate, setBlockAddLockInDate] = useState(null);
  const [blockAddCompanyIds, setBlockAddCompanyIds] = useState([]);
  const [marketingPartnersModalOpened, setMarketingPartnersModalOpened] =
    useState(false);
  const [mdScalesModalOpened, setMdScalesModalOpened] = useState(false);
  const [editingCompanyMdYear, setEditingCompanyMdYear] = useState(null);
  const [selectedMarketingPartners, setSelectedMarketingPartners] = useState(
    []
  );
  const [marketingPartners, setMarketingPartners] = useState([]);
  const [mdScales, setMdScales] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async event => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sellingYears.findIndex(y => y.id === active.id);
    const newIndex = sellingYears.findIndex(y => y.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const previousYears = sellingYears;
    const reordered = arrayMove(sellingYears, oldIndex, newIndex);
    setSellingYears(reordered);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setSellingYears(previousYears);
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/selling-year/reorder"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          selling_year_id: active.id,
          sort_index: newIndex,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setSellingYears(previousYears);
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to reorder selling years"
        );
      }
    } catch (err) {
      setSellingYears(previousYears);
      handleGenericError(err, "Failed to reorder selling years");
    }
  };

  const fetchMarketingPartners = async () => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/marketing-partner"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const partners = result.data || [];
        const activePartners = Array.isArray(partners)
          ? partners.filter(p => p.is_active !== false)
          : [];
        setMarketingPartners(activePartners);
      }
    } catch (err) {}
  };

  // Uploaded agreements are e-signature templates tagged with a selling_year_id.
  // We fetch them all here and filter by the active year when copying.
  const fetchUploadedAgreements = async () => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const result = await esignatureService.getTemplates(jwt);
      const templates = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.templates)
            ? result.data.templates
            : [];
      setUploadedAgreements(templates);
    } catch (err) {}
  };

  // Default scales, upfront pay, expenses and bonuses all come back on the
  // pay-structure overview. Each is offered for copying on its own wizard step.
  const fetchPayStructureDefaults = async () => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/ps/overview"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = (result && result.data) || result || {};
        setDefaultScales(
          Array.isArray(data.company_default_scales)
            ? data.company_default_scales
            : []
        );
        setUpfrontPay(Array.isArray(data.upfront_pay) ? data.upfront_pay : []);
        setDefaultExpenses(
          Array.isArray(data.default_expenses) ? data.default_expenses : []
        );
        setDefaultBonuses(
          Array.isArray(data.default_bonuses) ? data.default_bonuses : []
        );
      }
    } catch (err) {}
  };

  const fetchSellingYears = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch selling years");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/selling-year/company"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years =
          result && result.data && result.data.selling_years
            ? result.data.selling_years
            : [];
        setSellingYears(years);
        const activeYear = years.find(y => y.is_active);
        const defaultTab = activeYear
          ? activeYear.name
          : years.length > 0
            ? years[0].name
            : null;
        setActiveTab(defaultTab);
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to fetch selling years");
      }
    } catch (err) {
      handleGenericError(err, "Failed to fetch selling years");
      setError("Failed to fetch selling years");
    } finally {
      setLoading(false);
    }
  };

  const toISODateString = date => {
    if (!date) return null;
    if (typeof date === "string") {
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
      if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    }
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = dateStr => {
    if (!dateStr) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (match) {
      return new Date(
        parseInt(match[1], 10),
        parseInt(match[2], 10) - 1,
        parseInt(match[3], 10)
      );
    }
    return new Date(dateStr);
  };

  const handleOpenAddModal = () => {
    setNewYearName("");
    setNewYearStartDate(null);
    setNewYearEndDate(null);
    setAddStep("details");
    setAddMdScales([]);
    const activeYear = sellingYears.find(y => y.is_active);
    setCopyMarketingPartnerIds(
      (activeYear?.marketing_partners || []).map(mp =>
        String(mp.marketing_partner_id)
      )
    );
    setCopyAgreementIds([]);
    setCopyScaleIds([]);
    setCopyUpfrontIds([]);
    setCopyExpenseIds([]);
    setCopyBonusIds([]);
    setAddModalOpened(true);
  };

  // Toggle helper for the simple "select items to copy" steps.
  const toggleCopyId = (setIds, id) =>
    setIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  // Marketing partners chosen on step 1, resolved to MultiSelect options. These
  // are the only partners assignable to the new year's MD scales on step 2.
  const selectedPartnerOptions = copyMarketingPartnerIds
    .map(id => {
      const mp = marketingPartners.find(m => String(m.id) === id);
      return mp ? { value: id, label: mp.name } : null;
    })
    .filter(Boolean);

  // Uploaded agreements belonging to the active year — the ones offered for
  // copying on step 3.
  const activeYearAgreements = (() => {
    const activeYear = sellingYears.find(y => y.is_active);
    if (!activeYear) return [];
    return uploadedAgreements.filter(
      a => String(a.selling_year_id) === String(activeYear.id)
    );
  })();

  // Selectable tile list used by the upfront/expense/bonus copy steps.
  // `renderDetails` draws the per-item details under the name.
  const renderCopyTileList = (
    items,
    selectedIds,
    setIds,
    emptyText,
    renderDetails
  ) =>
    items.length === 0 ? (
      <Text size="sm" c="dimmed">
        {emptyText}
      </Text>
    ) : (
      <Stack gap="xs">
        {items.map(item => {
          const id = String(item.id);
          const selected = selectedIds.includes(id);
          return (
            <Card
              key={item.id}
              padding="sm"
              radius="md"
              withBorder
              onClick={() => toggleCopyId(setIds, id)}
              style={{
                cursor: "pointer",
                borderColor: selected
                  ? "var(--mantine-color-blue-5)"
                  : undefined,
                backgroundColor: selected
                  ? "var(--mantine-color-blue-0)"
                  : undefined,
              }}
            >
              <Stack gap="xs">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <IconCurrencyDollar size={18} />
                  <Text size="sm" fw={600} truncate>
                    {item.name ?? "Untitled"}
                  </Text>
                </Group>
                {renderDetails && renderDetails(item)}
              </Stack>
            </Card>
          );
        })}
      </Stack>
    );

  // Step 1 -> step 2: validate the details, then show the MD scale editor.
  // The first time, seed it from the active year's scales (dropping the source
  // ids). On later visits keep any in-progress edits — only drop partner
  // assignments for partners that were deselected on step 1.
  const handleAddNext = () => {
    const trimmedName = newYearName.trim();
    if (!trimmedName) {
      showError("Please enter a name");
      return;
    }
    if (!newYearStartDate || !newYearEndDate) {
      showError("Please select a start and end date");
      return;
    }

    const selected = new Set(copyMarketingPartnerIds);
    if (addMdScales.length === 0) {
      const activeYear = sellingYears.find(y => y.is_active);
      const seeded = (activeYear ? buildMdScaleGroups(activeYear) : []).map(
        group => ({
          scale_lines: group.scale_lines,
          marketing_partner_ids: group.marketing_partner_ids.filter(id =>
            selected.has(id)
          ),
        })
      );
      setAddMdScales(seeded.length > 0 ? seeded : [emptyMdScale()]);
    } else {
      setAddMdScales(prev =>
        prev.map(scale => ({
          ...scale,
          marketing_partner_ids: scale.marketing_partner_ids.filter(id =>
            selected.has(id)
          ),
        }))
      );
    }
    setAddStep("mdScales");
  };

  // Step 2 -> step 3: only proceed when the MD scales are valid — each scale has
  // at least one fully filled line, and every marketing partner selected on
  // step 1 is assigned to some scale.
  const handleMdScalesNext = () => {
    for (const scale of addMdScales) {
      if (!scale.scale_lines.length) {
        showError("Each MD scale must have at least one line");
        return;
      }
      for (const line of scale.scale_lines) {
        if (
          line.revenue_amount === "" ||
          line.revenue_amount == null ||
          line.percentage === "" ||
          line.percentage == null
        ) {
          showError("Please fill out every MD scale line");
          return;
        }
      }
    }

    const assigned = new Set(
      addMdScales.flatMap(scale => scale.marketing_partner_ids)
    );
    const unassigned = copyMarketingPartnerIds.filter(id => !assigned.has(id));
    if (unassigned.length > 0) {
      showError("Every selected marketing partner must be assigned to a scale");
      return;
    }

    setAddStep("agreements");
  };

  const handleCreateSellingYear = async () => {
    const trimmedName = newYearName.trim();
    if (!trimmedName) {
      showError("Please enter a name");
      return;
    }
    if (!newYearStartDate || !newYearEndDate) {
      showError("Please select a start and end date");
      return;
    }
    for (const scale of addMdScales) {
      for (const line of scale.scale_lines) {
        if (
          line.revenue_amount === "" ||
          line.revenue_amount == null ||
          line.percentage === "" ||
          line.percentage == null
        ) {
          showError("Please fill out every MD scale line");
          return;
        }
      }
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const activeYear = sellingYears.find(y => y.is_active);
      const response = await fetch(getApiEndpoint("/v1/selling-year"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          start_date: toISODateString(newYearStartDate),
          end_date: toISODateString(newYearEndDate),
          ...(activeYear
            ? {
                marketing_partner_ids: copyMarketingPartnerIds.map(Number),
                // Agreement (e-signature template) ids may be non-numeric, so
                // send them as-is rather than coercing to numbers.
                uploaded_document_template_ids: copyAgreementIds,
                default_scale_ids: copyScaleIds.map(Number),
                upfront_pay_ids: copyUpfrontIds.map(Number),
                default_expense_ids: copyExpenseIds.map(Number),
                default_bonus_ids: copyBonusIds.map(Number),
              }
            : {}),
          company_md_scales: addMdScales.map(scale => ({
            scale_lines: scale.scale_lines.map(line => ({
              revenue_amount: line.revenue_amount,
              percentage: line.percentage,
            })),
            marketing_partner_ids: scale.marketing_partner_ids.map(Number),
          })),
        }),
      });

      if (response.ok) {
        showSuccess("Selling year created successfully!");
        setAddModalOpened(false);
        setNewYearName("");
        setNewYearStartDate(null);
        setNewYearEndDate(null);
        setAddStep("details");
        setAddMdScales([]);
        setCopyMarketingPartnerIds([]);
        setCopyAgreementIds([]);
        setCopyScaleIds([]);
        setCopyUpfrontIds([]);
        setCopyExpenseIds([]);
        setCopyBonusIds([]);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to create selling year"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to create selling year");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditModal = year => {
    setEditingYear(year);
    setEditYearName(year.name || "");
    setEditYearStartDate(toISODateString(year.start_date));
    setEditYearEndDate(toISODateString(year.end_date));
    setEditYearLocked(!!year.is_locked);
    setEditYearActive(!!year.is_active);
    setEditModalOpened(true);
  };

  const handleUpdateSellingYear = async () => {
    if (!editingYear) return;
    const trimmedName = editYearName.trim();
    if (!trimmedName) {
      showError("Please enter a name");
      return;
    }
    if (!editYearStartDate || !editYearEndDate) {
      showError("Please select a start and end date");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/selling-year/${editingYear.id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            name: trimmedName,
            start_date: toISODateString(editYearStartDate),
            end_date: toISODateString(editYearEndDate),
            is_locked: editYearLocked,
            is_active: editYearActive,
          }),
        }
      );

      if (response.ok) {
        showSuccess("Selling year updated successfully!");
        setEditModalOpened(false);
        setEditingYear(null);
        setEditYearName("");
        setEditYearStartDate(null);
        setEditYearEndDate(null);
        setEditYearLocked(false);
        setEditYearActive(false);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update selling year"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update selling year");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteModal = year => {
    setDeletingYear(year);
    setDeleteModalOpened(true);
  };

  const handleDeleteSellingYear = async () => {
    if (!deletingYear) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/selling-year/${deletingYear.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        showSuccess("Selling year deleted successfully!");
        setDeleteModalOpened(false);
        setDeletingYear(null);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete selling year"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete selling year");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenBlockAddModal = yearId => {
    setAddingBlockYearId(yearId);
    setBlockAddName("");
    setBlockAddStartDate(null);
    setBlockAddEndDate(null);
    setBlockAddLockInDate(null);
    setBlockAddCompanyIds([]);
    setBlockAddModalOpened(true);
  };

  const handleCreateBlock = async () => {
    const trimmedName = blockAddName.trim();
    if (!trimmedName) {
      showError("Please enter a name");
      return;
    }
    if (!blockAddStartDate || !blockAddEndDate || !blockAddLockInDate) {
      showError("Please select a start, end, and lock in date");
      return;
    }
    if (!blockAddCompanyIds.length) {
      showError("Please select at least one marketing partner");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/selling-year/${addingBlockYearId}/block`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            name: trimmedName,
            start_date: toISODateString(blockAddStartDate),
            end_date: toISODateString(blockAddEndDate),
            lock_in_date: toISODateString(blockAddLockInDate),
            marketing_partner_ids: blockAddCompanyIds.map(Number),
          }),
        }
      );

      if (response.ok) {
        showSuccess("Selling block created successfully!");
        setBlockAddModalOpened(false);
        setAddingBlockYearId(null);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to create selling block"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to create selling block");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenBlockEditModal = (block, yearId) => {
    setEditingBlock(block);
    setEditingBlockYearId(yearId);
    setBlockEditName(block.name || "");
    setBlockEditStartDate(toISODateString(block.start_date));
    setBlockEditEndDate(toISODateString(block.end_date));
    setBlockEditLockInDate(toISODateString(block.lock_in_date));
    setBlockEditCompanyIds(
      Array.isArray(block.marketing_partners)
        ? block.marketing_partners.map(mp =>
            String(mp.marketing_partner_id ?? mp.id)
          )
        : []
    );
    setBlockEditModalOpened(true);
  };

  const handleUpdateBlock = async () => {
    if (!editingBlock) return;
    const trimmedName = blockEditName.trim();
    if (!trimmedName) {
      showError("Please enter a name");
      return;
    }
    if (!blockEditStartDate || !blockEditEndDate || !blockEditLockInDate) {
      showError("Please select a start, end, and lock in date");
      return;
    }
    if (!blockEditCompanyIds.length) {
      showError("Please select at least one marketing partner");
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(
          `/v1/selling-year/${editingBlockYearId}/block/${editingBlock.id}`
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            selling_year_block_id: editingBlock.id,
            name: trimmedName,
            start_date: toISODateString(blockEditStartDate),
            end_date: toISODateString(blockEditEndDate),
            lock_in_date: toISODateString(blockEditLockInDate),
            marketing_partner_ids: blockEditCompanyIds.map(Number),
          }),
        }
      );

      if (response.ok) {
        showSuccess("Selling block updated successfully!");
        setBlockEditModalOpened(false);
        setEditingBlock(null);
        setEditingBlockYearId(null);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update selling block"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update selling block");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlock = async () => {
    if (!editingBlock) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(
          `/v1/selling-year/${editingBlockYearId}/block/${editingBlock.id}`
        ),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        showSuccess("Selling block deleted successfully!");
        setBlockEditModalOpened(false);
        setEditingBlock(null);
        setEditingBlockYearId(null);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete selling block"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete selling block");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenMarketingPartnersModal = year => {
    setEditingCompanyMdYear(year);
    // The operations marketing partner is managed separately, so keep it out of
    // the editable selection.
    const operationsId = marketingPartners.find(mp => mp.is_operations_mp)?.id;
    setSelectedMarketingPartners(
      (year.marketing_partners || [])
        .filter(mp => String(mp.marketing_partner_id) !== String(operationsId))
        .map(mp => String(mp.marketing_partner_id))
    );
    setMarketingPartnersModalOpened(true);
  };

  const handleOpenMdScalesModal = year => {
    setEditingCompanyMdYear(year);

    // The company MD scales are already returned with the selling-year/company
    // call on company_deal.deal_scales — no extra request needed.
    const grouped = buildMdScaleGroups(year);
    setMdScales(grouped.length > 0 ? grouped : [emptyMdScale()]);
    setMdScalesModalOpened(true);
  };

  const handleSaveMarketingPartners = async () => {
    if (!editingCompanyMdYear) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/selling-year/marketing-partner`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            selling_year_id: editingCompanyMdYear.id,
            marketing_partner_ids: selectedMarketingPartners.map(id =>
              Number(id)
            ),
          }),
        }
      );

      if (response.ok) {
        showSuccess("Marketing partners updated successfully!");
        setMarketingPartnersModalOpened(false);
        setEditingCompanyMdYear(null);
        setSelectedMarketingPartners([]);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update marketing partners"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update marketing partners");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMdScales = async () => {
    if (!editingCompanyMdYear) return;

    try {
      setSubmitting(true);

      // Validate that every scale line is filled out
      for (const scale of mdScales) {
        for (const line of scale.scale_lines) {
          if (
            line.revenue_amount === "" ||
            line.revenue_amount == null ||
            line.percentage === "" ||
            line.percentage == null
          ) {
            showError("Please fill out every scale line");
            return;
          }
        }
      }

      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Each scale is sent as a group of scale lines plus the marketing
      // partners it applies to.
      const scalesData = mdScales.map(scale => ({
        ...(typeof scale.id === "number" ? { id: scale.id } : {}),
        scale_lines: scale.scale_lines.map(line => ({
          revenue_amount: line.revenue_amount,
          percentage: line.percentage,
        })),
        marketing_partner_ids: scale.marketing_partner_ids.map(id =>
          Number(id)
        ),
      }));

      const response = await fetch(
        getApiEndpoint(
          `/v1/selling-year/${editingCompanyMdYear.id}/company-md`
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            scales: scalesData,
          }),
        }
      );

      if (response.ok) {
        showSuccess("MD scales updated successfully!");
        setMdScalesModalOpened(false);
        setEditingCompanyMdYear(null);
        setMdScales([]);
        fetchSellingYears();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update MD scales"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update MD scales");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchSellingYears();
    fetchMarketingPartners();
    fetchUploadedAgreements();
    fetchPayStructureDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    try {
      const d = parseLocalDate(dateString);
      if (!d) return "N/A";
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const renderMdScales = year => {
    const groups = buildMdScaleGroups(year);
    if (groups.length === 0) {
      return (
        <Text size="xs" c="dimmed">
          No MD scales configured
        </Text>
      );
    }

    const partnerName = id => {
      const mp = (year.marketing_partners || []).find(
        m => String(m.marketing_partner_id) === String(id)
      );
      return mp?.name || id;
    };

    const operationsId = marketingPartners.find(mp => mp.is_operations_mp)?.id;
    const isOperationsId = id => String(id) === String(operationsId);

    return (
      <Stack gap="xs">
        {groups.map((scale, index) => (
          <Card key={scale.id ?? index} padding="sm" radius="md" withBorder>
            <Stack gap="xs">
              <Stack gap={4}>
                {scale.scale_lines.map((line, i) => (
                  <Group key={i} gap="xs">
                    <Text size="sm" c="dimmed">
                      {formatScaleCurrency(line.revenue_amount)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      →
                    </Text>
                    <Text size="sm">{formatScalePercent(line.percentage)}</Text>
                  </Group>
                ))}
              </Stack>
              {scale.marketing_partner_ids.length > 0 && (
                <Group gap="xs">
                  {scale.marketing_partner_ids.map(id => (
                    <Badge
                      key={id}
                      variant="light"
                      color={isOperationsId(id) ? "grape" : undefined}
                    >
                      {partnerName(id)}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderSellingYearContent = year => {
    const blocks = Array.isArray(year.selling_year_blocks)
      ? year.selling_year_blocks
      : [];

    return (
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            <Title order={3}>{year.name}</Title>
            {year.is_active && (
              <Badge color="green" variant="light">
                Active
              </Badge>
            )}
            {year.is_locked && (
              <Badge color="gray" variant="filled">
                Locked
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Button
              variant="outline"
              leftSection={<IconEdit size={16} />}
              onClick={() => handleOpenEditModal(year)}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => handleOpenDeleteModal(year)}
            >
              Delete
            </Button>
          </Group>
        </Group>

        <Group gap="xl">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Start:
            </Text>
            <Text size="sm">{formatDate(year.start_date)}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              End:
            </Text>
            <Text size="sm">{formatDate(year.end_date)}</Text>
          </Group>
        </Group>

        <CollapsibleCard title="Company MD">
          <Stack gap="lg">
            {(() => {
              // is_operations_mp is a company-level flag on the master marketing
              // partner list, so resolve it from there (not the year's assigned
              // partners, which don't carry it).
              const operationsPartner = marketingPartners.find(
                mp => mp.is_operations_mp
              );
              const allPartners = Array.isArray(year.marketing_partners)
                ? year.marketing_partners
                : [];
              const otherPartners = operationsPartner
                ? allPartners.filter(
                    mp =>
                      String(mp.marketing_partner_id) !==
                      String(operationsPartner.id)
                  )
                : allPartners;
              return (
                <>
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Company Operations Marketing Partner
                    </Text>
                    {operationsPartner ? (
                      <Group gap="xs">
                        <Badge variant="light" color="grape">
                          {operationsPartner.name}
                        </Badge>
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed">
                        No operations marketing partner selected
                      </Text>
                    )}
                  </Stack>
                  <Stack gap="xs">
                    <Group justify="space-between" align="center">
                      <Text size="sm" fw={600}>
                        Marketing Partners
                      </Text>
                      {!year.is_locked && (
                        <Button
                          variant="light"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={() => handleOpenMarketingPartnersModal(year)}
                        >
                          Edit
                        </Button>
                      )}
                    </Group>
                    {otherPartners.length > 0 ? (
                      <Group gap="xs">
                        {otherPartners.map(mp => (
                          <Badge key={mp.id} variant="light">
                            {mp.name}
                          </Badge>
                        ))}
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed">
                        No marketing partners selected
                      </Text>
                    )}
                  </Stack>
                </>
              );
            })()}
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  MD Scales
                </Text>
                {!year.is_locked && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => handleOpenMdScalesModal(year)}
                  >
                    Edit
                  </Button>
                )}
              </Group>
              {renderMdScales(year)}
            </Stack>
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  Selling Blocks
                </Text>
                {!year.is_locked && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => handleOpenBlockAddModal(year.id)}
                  >
                    Add Block
                  </Button>
                )}
              </Group>
              {blocks.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No selling blocks in this selling year.
                </Text>
              ) : (
                <Stack gap="xs">
                  {blocks.map((block, index) => (
                    <Card
                      key={block.id ?? `${block.name}-${index}`}
                      padding="sm"
                      radius="md"
                      withBorder
                    >
                      <Stack gap="xs">
                        <Group justify="space-between" align="center">
                          <Text size="sm" fw={600}>
                            {block.name}
                          </Text>
                          <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<IconEdit size={14} />}
                            onClick={() =>
                              handleOpenBlockEditModal(block, year.id)
                            }
                          >
                            Edit
                          </Button>
                        </Group>
                        <Group gap="xl">
                          <Group gap="xs">
                            <Text size="sm" c="dimmed">
                              Start:
                            </Text>
                            <Text size="sm">
                              {formatDate(block.start_date)}
                            </Text>
                          </Group>
                          <Group gap="xs">
                            <Text size="sm" c="dimmed">
                              End:
                            </Text>
                            <Text size="sm">{formatDate(block.end_date)}</Text>
                          </Group>
                          <Group gap="xs">
                            <Text size="sm" c="dimmed">
                              Lock In:
                            </Text>
                            <Text size="sm">
                              {formatDate(block.lock_in_date)}
                            </Text>
                          </Group>
                          <Group gap="xs">
                            <Text size="sm" c="dimmed">
                              Marketing Partners:
                            </Text>
                            <Text size="sm">
                              {Array.isArray(block.marketing_partners) &&
                              block.marketing_partners.length > 0
                                ? block.marketing_partners
                                    .map(mp => mp.name)
                                    .join(", ")
                                : "N/A"}
                            </Text>
                          </Group>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </CollapsibleCard>
      </Stack>
    );
  };

  return (
    <PageContent title="Selling Years">
      <Stack gap="lg">
        <Group justify="flex-end">
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleOpenAddModal}
          >
            Add Selling Year
          </Button>
        </Group>
        <Card padding="lg" radius="md" withBorder>
          {loading && (
            <Group justify="center" py="xl">
              <Loader size="md" />
              <Text>Loading selling years...</Text>
            </Group>
          )}

          {error && !loading && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
            >
              <Group justify="space-between">
                <Text>{error}</Text>
                <Button
                  variant="outline"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={fetchSellingYears}
                >
                  Retry
                </Button>
              </Group>
            </Alert>
          )}

          {!loading && !error && sellingYears.length === 0 && (
            <Text ta="center" c="dimmed" py="xl">
              No selling years found.
            </Text>
          )}

          {!loading && !error && sellingYears.length > 0 && activeTab && (
            <Tabs value={activeTab} onChange={setActiveTab}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sellingYears.map(y => y.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <Tabs.List>
                    {sellingYears.map(year => (
                      <SortableTab
                        key={year.id}
                        year={year}
                        isActive={activeTab === year.name}
                      />
                    ))}
                  </Tabs.List>
                </SortableContext>
              </DndContext>

              {sellingYears.map(year => (
                <Tabs.Panel key={year.id} value={year.name} pt="lg">
                  {renderSellingYearContent(year)}
                </Tabs.Panel>
              ))}
            </Tabs>
          )}
        </Card>
      </Stack>

      <Modal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        title={
          addStep === "mdScales"
            ? "Add Selling Year — MD Scales"
            : addStep === "agreements"
              ? "Add Selling Year — Uploaded Agreements"
              : addStep === "scales"
                ? "Add Selling Year — Default Scales"
                : addStep === "upfront"
                  ? "Add Selling Year — Upfront Pay"
                  : addStep === "expenses"
                    ? "Add Selling Year — Expenses"
                    : addStep === "bonuses"
                      ? "Add Selling Year — Bonuses"
                      : "Add Selling Year"
        }
        size={addStep === "mdScales" ? "lg" : "md"}
      >
        {(() => {
          const activeYear = sellingYears.find(y => y.is_active);

          if (addStep === "agreements") {
            return (
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Copy default uploaded agreements
                </Text>
                {activeYearAgreements.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    The active year has no uploaded agreements to copy.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {activeYearAgreements.map(agreement => {
                      const id = String(agreement.id);
                      const selected = copyAgreementIds.includes(id);
                      return (
                        <Card
                          key={agreement.id}
                          padding="sm"
                          radius="md"
                          withBorder
                          onClick={() =>
                            setCopyAgreementIds(prev =>
                              selected
                                ? prev.filter(x => x !== id)
                                : [...prev, id]
                            )
                          }
                          style={{
                            cursor: "pointer",
                            borderColor: selected
                              ? "var(--mantine-color-blue-5)"
                              : undefined,
                            backgroundColor: selected
                              ? "var(--mantine-color-blue-0)"
                              : undefined,
                          }}
                        >
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                            <IconFileText size={18} />
                            <Text size="sm" fw={600} truncate>
                              {agreement.name ?? "Untitled Agreement"}
                            </Text>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("mdScales")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button onClick={() => setAddStep("scales")}>Next</Button>
                </Group>
              </Stack>
            );
          }

          if (addStep === "scales") {
            const scalesByType = SCALE_TYPE_SECTIONS.map(section => ({
              ...section,
              scales: defaultScales.filter(s => s.scale_type === section.type),
            })).filter(section => section.scales.length > 0);

            return (
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Copy default scales
                </Text>
                {scalesByType.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    There are no default scales to copy.
                  </Text>
                ) : (
                  scalesByType.map(section => (
                    <Stack key={section.type} gap="xs">
                      <Text size="sm" fw={600} c="dimmed">
                        {section.label}
                      </Text>
                      {section.scales.map(scale => {
                        const id = String(scale.id);
                        const selected = copyScaleIds.includes(id);
                        const lines = (
                          Array.isArray(scale.default_scale_lines)
                            ? scale.default_scale_lines
                            : scale.scale_lines || []
                        )
                          .slice()
                          .sort(
                            (a, b) =>
                              (a.revenue_amount || 0) - (b.revenue_amount || 0)
                          );
                        return (
                          <Card
                            key={scale.id}
                            padding="sm"
                            radius="md"
                            withBorder
                            onClick={() =>
                              setCopyScaleIds(prev =>
                                selected
                                  ? prev.filter(x => x !== id)
                                  : [...prev, id]
                              )
                            }
                            style={{
                              cursor: "pointer",
                              borderColor: selected
                                ? "var(--mantine-color-blue-5)"
                                : undefined,
                              backgroundColor: selected
                                ? "var(--mantine-color-blue-0)"
                                : undefined,
                            }}
                          >
                            <Stack gap="xs">
                              <Group
                                gap="xs"
                                wrap="nowrap"
                                style={{ minWidth: 0 }}
                              >
                                <IconCurrencyDollar size={18} />
                                <Text size="sm" fw={600} truncate>
                                  {scale.name ?? "Untitled Scale"}
                                </Text>
                              </Group>
                              {lines.length > 0 && (
                                <Stack gap={2}>
                                  {lines.map((line, lineIndex) => (
                                    <Group key={line.id ?? lineIndex} gap="xs">
                                      <Text size="sm" c="dimmed">
                                        {formatScaleCurrency(
                                          line.revenue_amount
                                        )}
                                      </Text>
                                      <Text size="sm" c="dimmed">
                                        →
                                      </Text>
                                      <Text size="sm">
                                        {formatScalePercent(line.percentage)}
                                      </Text>
                                    </Group>
                                  ))}
                                </Stack>
                              )}
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  ))
                )}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("agreements")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button onClick={() => setAddStep("upfront")}>Next</Button>
                </Group>
              </Stack>
            );
          }

          if (addStep === "upfront") {
            return (
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Copy upfront pay
                </Text>
                {renderCopyTileList(
                  upfrontPay,
                  copyUpfrontIds,
                  setCopyUpfrontIds,
                  "There is no upfront pay to copy.",
                  renderUpfrontDetails
                )}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("scales")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button onClick={() => setAddStep("expenses")}>Next</Button>
                </Group>
              </Stack>
            );
          }

          if (addStep === "expenses") {
            return (
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Copy default expenses
                </Text>
                {renderCopyTileList(
                  defaultExpenses,
                  copyExpenseIds,
                  setCopyExpenseIds,
                  "There are no default expenses to copy.",
                  renderExpenseDetails
                )}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("upfront")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button onClick={() => setAddStep("bonuses")}>Next</Button>
                </Group>
              </Stack>
            );
          }

          if (addStep === "bonuses") {
            return (
              <Stack gap="md">
                <Text size="sm" fw={600}>
                  Copy default bonuses
                </Text>
                {renderCopyTileList(
                  defaultBonuses,
                  copyBonusIds,
                  setCopyBonusIds,
                  "There are no default bonuses to copy.",
                  renderBonusDetails
                )}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("expenses")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCreateSellingYear}
                    loading={submitting}
                  >
                    Create
                  </Button>
                </Group>
              </Stack>
            );
          }

          if (addStep === "mdScales") {
            return (
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Review and edit the MD scales copied from the active year
                  {activeYear ? ` (${activeYear.name})` : ""}.
                </Text>
                <MdScalesEditor
                  scales={addMdScales}
                  onChange={setAddMdScales}
                  partnerOptions={selectedPartnerOptions}
                />
                <Group justify="space-between" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => setAddStep("details")}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button onClick={handleMdScalesNext}>Next</Button>
                </Group>
              </Stack>
            );
          }

          return (
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="e.g., 2025-2026"
                value={newYearName}
                onChange={e => setNewYearName(e.target.value)}
                required
                data-autofocus
              />
              <DateInput
                label="Start Date"
                placeholder="Select start date"
                value={newYearStartDate}
                onChange={setNewYearStartDate}
                required
              />
              <DateInput
                label="End Date"
                placeholder="Select end date"
                value={newYearEndDate}
                onChange={setNewYearEndDate}
                required
              />
              {activeYear && marketingPartners.length > 0 && (
                <>
                  <Divider />
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Copy marketing partners from active year
                    </Text>
                    {marketingPartners.map(mp => {
                      const id = String(mp.id);
                      return (
                        <Checkbox
                          key={mp.id}
                          label={mp.name}
                          checked={copyMarketingPartnerIds.includes(id)}
                          onChange={e =>
                            setCopyMarketingPartnerIds(prev =>
                              e.currentTarget.checked
                                ? [...prev, id]
                                : prev.filter(x => x !== id)
                            )
                          }
                        />
                      );
                    })}
                  </Stack>
                </>
              )}
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={() => setAddModalOpened(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                {activeYear ? (
                  <Button onClick={handleAddNext}>Next</Button>
                ) : (
                  <Button
                    onClick={handleCreateSellingYear}
                    loading={submitting}
                  >
                    Create
                  </Button>
                )}
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      <Modal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        title="Edit Selling Year"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g., 2025-2026"
            value={editYearName}
            onChange={e => setEditYearName(e.target.value)}
            required
            data-autofocus
          />
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            value={editYearStartDate}
            onChange={setEditYearStartDate}
            required
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            value={editYearEndDate}
            onChange={setEditYearEndDate}
            required
          />
          <Switch
            label="Locked"
            checked={editYearLocked}
            onChange={e => setEditYearLocked(e.currentTarget.checked)}
          />
          <Switch
            label="Active"
            checked={editYearActive}
            onChange={e => setEditYearActive(e.currentTarget.checked)}
          />
          {editYearActive && !editingYear?.is_active && (
            <Text size="sm" c="dimmed">
              Making this the active season will mark all other seasons as
              inactive.
            </Text>
          )}
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setEditModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateSellingYear} loading={submitting}>
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete Selling Year"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the selling year "
            {deletingYear?.name}"? This action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setDeleteModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteSellingYear}
              loading={submitting}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={blockEditModalOpened}
        onClose={() => setBlockEditModalOpened(false)}
        title="Edit Selling Block"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g., Block 1"
            value={blockEditName}
            onChange={e => setBlockEditName(e.target.value)}
            required
            data-autofocus
          />
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            value={blockEditStartDate}
            onChange={setBlockEditStartDate}
            required
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            value={blockEditEndDate}
            onChange={setBlockEditEndDate}
            required
          />
          <DateInput
            label="Lock In Date"
            placeholder="Select lock in date"
            value={blockEditLockInDate}
            onChange={setBlockEditLockInDate}
            required
          />
          <MultiSelect
            label="Marketing Partners"
            placeholder="Select marketing partners"
            data={(
              sellingYears.find(y => y.id === editingBlockYearId)
                ?.marketing_partners || []
            ).map(mp => ({
              value: String(mp.marketing_partner_id),
              label: mp.name,
            }))}
            value={blockEditCompanyIds}
            onChange={setBlockEditCompanyIds}
            searchable
            required
          />
          <Group justify="space-between" mt="md">
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteBlock}
              loading={submitting}
            >
              Delete
            </Button>
            <Group>
              <Button
                variant="subtle"
                onClick={() => setBlockEditModalOpened(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateBlock} loading={submitting}>
                Update
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={blockAddModalOpened}
        onClose={() => setBlockAddModalOpened(false)}
        title="Add Selling Block"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g., Block 1"
            value={blockAddName}
            onChange={e => setBlockAddName(e.target.value)}
            required
            data-autofocus
          />
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            value={blockAddStartDate}
            onChange={setBlockAddStartDate}
            required
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            value={blockAddEndDate}
            onChange={setBlockAddEndDate}
            required
          />
          <DateInput
            label="Lock In Date"
            placeholder="Select lock in date"
            value={blockAddLockInDate}
            onChange={setBlockAddLockInDate}
            required
          />
          <MultiSelect
            label="Marketing Partners"
            placeholder="Select marketing partners"
            data={(
              sellingYears.find(y => y.id === addingBlockYearId)
                ?.marketing_partners || []
            ).map(mp => ({
              value: String(mp.marketing_partner_id),
              label: mp.name,
            }))}
            value={blockAddCompanyIds}
            onChange={setBlockAddCompanyIds}
            searchable
            required
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setBlockAddModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateBlock} loading={submitting}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={marketingPartnersModalOpened}
        onClose={() => setMarketingPartnersModalOpened(false)}
        title="Edit Marketing Partners"
        size="md"
      >
        <Stack gap="md">
          <MultiSelect
            label="Marketing Partners"
            placeholder="Select marketing partners"
            data={marketingPartners
              .filter(mp => !mp.is_operations_mp)
              .map(mp => ({
                value: String(mp.id),
                label: mp.name,
              }))}
            value={selectedMarketingPartners}
            onChange={setSelectedMarketingPartners}
            searchable
            clearable
          />

          <div>
            <Text size="sm" fw={500}>
              Company Operations
            </Text>
            {(() => {
              // is_operations_mp is carried on the master marketing partner list,
              // not the year's assigned partners.
              const operationsPartner = marketingPartners.find(
                mp => mp.is_operations_mp
              );
              return (
                <Text size="sm" c={operationsPartner ? undefined : "dimmed"}>
                  {operationsPartner
                    ? operationsPartner.name
                    : "No operations marketing partner selected"}
                </Text>
              );
            })()}
          </div>

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setMarketingPartnersModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveMarketingPartners} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={mdScalesModalOpened}
        onClose={() => setMdScalesModalOpened(false)}
        title="Edit MD Scales"
        size="lg"
      >
        <Stack gap="md">
          <MdScalesEditor
            scales={mdScales}
            onChange={setMdScales}
            partnerOptions={(() => {
              const options = (
                editingCompanyMdYear?.marketing_partners || []
              ).map(mp => ({
                value: String(mp.marketing_partner_id),
                label: mp.name,
              }));
              // The operations marketing partner is managed separately and isn't
              // part of the year's marketing partners, but it can still be
              // assigned to an MD scale.
              const operationsMp = marketingPartners.find(
                mp => mp.is_operations_mp
              );
              if (
                operationsMp &&
                !options.some(opt => opt.value === String(operationsMp.id))
              ) {
                options.push({
                  value: String(operationsMp.id),
                  label: operationsMp.name,
                });
              }
              return options;
            })()}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setMdScalesModalOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveMdScales} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default SellingYearsPage;
